// ProductListContext.tsx
import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react'
import { Product as IProduct } from '../interfaces/definitions/product.interface'

interface ProductListContextType {
  products: IProduct[]
  setProducts: React.Dispatch<React.SetStateAction<IProduct[]>>
  lastKey: string | undefined
  setLastKey: React.Dispatch<React.SetStateAction<string | undefined>>
  hasMore: boolean
  setHasMore: React.Dispatch<React.SetStateAction<boolean>>
  search: string | undefined
  setSearch: React.Dispatch<React.SetStateAction<string | undefined>>
  isSearching: boolean
  setIsSearching: React.Dispatch<React.SetStateAction<boolean>>
  searchHistory: string[]
  setSearchHistory: React.Dispatch<React.SetStateAction<string[]>>
  addSearchToHistory: (search: string) => void
}

const ProductListContext = createContext<ProductListContextType | undefined>(undefined)
const LOCAL_STORAGE_KEY = 'searchHistory'

export const ProductListProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<IProduct[]>([])
  const [lastKey, setLastKey] = useState<string | undefined>(undefined)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [search, setSearch] = useState<string | undefined>()
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [searchHistory, setSearchHistory] = useState<string[]>([])

  useEffect(() => {
    const storedHistory = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (storedHistory != null) {
      setSearchHistory(JSON.parse(storedHistory))
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(searchHistory))
  }, [searchHistory])

  const addSearchToHistory = (search: string): void => {
    setSearchHistory(prevHistory => {
      const updatedHistory = [search, ...prevHistory.filter(item => item !== search)].slice(0, 10)
      return updatedHistory
    })
  }

  return (
    <ProductListContext.Provider value={{
      products,
      setProducts,
      lastKey,
      setLastKey,
      hasMore,
      setHasMore,
      search,
      setSearch,
      isSearching,
      setIsSearching,
      searchHistory,
      setSearchHistory,
      addSearchToHistory
    }}
    >
      {children}
    </ProductListContext.Provider>
  )
}

export const useProductList = (): ProductListContextType => {
  const context = useContext(ProductListContext)
  if (context === undefined) {
    throw new Error('useProductList must be used within a ProductListProvider')
  }
  return context
}
